import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useI18n } from '../context/I18nContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { BookOpen, FileText, ExternalLink, Search, Tag } from 'lucide-react';
import { API } from '../config';
import axios from 'axios';

const labels = {
  en: { title: 'Resources', subtitle: 'Help documents, guides and tutorials to get the most out of HexoPDF.', search: 'Search resources...', empty: 'No resources available yet.', categories: { guide: 'Guide', tutorial: 'Tutorial', faq: 'FAQ', documentation: 'Documentation', other: 'Other' } },
  fr: { title: 'Ressources', subtitle: "Documents d'aide, guides et tutoriels pour tirer le meilleur parti de HexoPDF.", search: 'Rechercher...', empty: 'Aucune ressource disponible pour le moment.', categories: { guide: 'Guide', tutorial: 'Tutoriel', faq: 'FAQ', documentation: 'Documentation', other: 'Autre' } },
  es: { title: 'Recursos', subtitle: 'Documentos de ayuda, guías y tutoriales para aprovechar HexoPDF.', search: 'Buscar...', empty: 'No hay recursos disponibles.', categories: { guide: 'Guía', tutorial: 'Tutorial', faq: 'FAQ', documentation: 'Documentación', other: 'Otro' } },
  de: { title: 'Ressourcen', subtitle: 'Hilfedokumente, Anleitungen und Tutorials für HexoPDF.', search: 'Suchen...', empty: 'Noch keine Ressourcen verfügbar.', categories: { guide: 'Anleitung', tutorial: 'Tutorial', faq: 'FAQ', documentation: 'Dokumentation', other: 'Sonstiges' } },
  it: { title: 'Risorse', subtitle: 'Documenti di aiuto, guide e tutorial per HexoPDF.', search: 'Cerca...', empty: 'Nessuna risorsa disponibile.', categories: { guide: 'Guida', tutorial: 'Tutorial', faq: 'FAQ', documentation: 'Documentazione', other: 'Altro' } },
  pt: { title: 'Recursos', subtitle: 'Documentos de ajuda, guias e tutoriais para HexoPDF.', search: 'Pesquisar...', empty: 'Nenhum recurso disponível.', categories: { guide: 'Guia', tutorial: 'Tutorial', faq: 'FAQ', documentation: 'Documentação', other: 'Outro' } },
  nl: { title: 'Bronnen', subtitle: 'Hulpdocumenten, handleidingen en tutorials voor HexoPDF.', search: 'Zoeken...', empty: 'Nog geen bronnen beschikbaar.', categories: { guide: 'Handleiding', tutorial: 'Tutorial', faq: 'FAQ', documentation: 'Documentatie', other: 'Anders' } },
  pl: { title: 'Zasoby', subtitle: 'Dokumenty pomocy, przewodniki i samouczki HexoPDF.', search: 'Szukaj...', empty: 'Brak dostępnych zasobów.', categories: { guide: 'Przewodnik', tutorial: 'Samouczek', faq: 'FAQ', documentation: 'Dokumentacja', other: 'Inne' } },
  tr: { title: 'Kaynaklar', subtitle: 'HexoPDF için yardım belgeleri, kılavuzlar ve öğreticiler.', search: 'Ara...', empty: 'Henüz kaynak yok.', categories: { guide: 'Kılavuz', tutorial: 'Öğretici', faq: 'SSS', documentation: 'Belgeleme', other: 'Diğer' } },
  ru: { title: 'Ресурсы', subtitle: 'Справочные документы, руководства и учебные пособия по HexoPDF.', search: 'Поиск...', empty: 'Ресурсы пока недоступны.', categories: { guide: 'Руководство', tutorial: 'Учебник', faq: 'FAQ', documentation: 'Документация', other: 'Другое' } },
  ja: { title: 'リソース', subtitle: 'HexoPDFのヘルプドキュメント、ガイド、チュートリアル。', search: '検索...', empty: 'リソースはまだありません。', categories: { guide: 'ガイド', tutorial: 'チュートリアル', faq: 'FAQ', documentation: 'ドキュメント', other: 'その他' } },
  zh: { title: '资源', subtitle: 'HexoPDF帮助文档、指南和教程。', search: '搜索...', empty: '暂无可用资源。', categories: { guide: '指南', tutorial: '教程', faq: 'FAQ', documentation: '文档', other: '其他' } },
  ar: { title: 'الموارد', subtitle: 'مستندات المساعدة والأدلة والبرامج التعليمية لـ HexoPDF.', search: 'بحث...', empty: 'لا توجد موارد متاحة حتى الآن.', categories: { guide: 'دليل', tutorial: 'تعليمي', faq: 'FAQ', documentation: 'توثيق', other: 'أخرى' } },
  hi: { title: 'संसाधन', subtitle: 'HexoPDF से अधिकतम लाभ लेने के लिए सहायता दस्तावेज़, गाइड और ट्यूटोरियल।', search: 'खोजें...', empty: 'अभी कोई संसाधन उपलब्ध नहीं।', categories: { guide: 'गाइड', tutorial: 'ट्यूटोरियल', faq: 'FAQ', documentation: 'दस्तावेज़ीकरण', other: 'अन्य' } },
};

const categoryIcons = { guide: BookOpen, tutorial: FileText, faq: Tag, documentation: FileText, other: Tag };

export default function ResourcesPage() {
  const { lang } = useI18n();
  const l = labels[lang] || labels.en;
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [filterLang, setFilterLang] = useState('all');

  useEffect(() => {
    axios.get(`${API}/resources`).then(r => setResources(r.data.resources || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = resources.filter(r => {
    const matchSearch = !search || r.title?.toLowerCase().includes(search.toLowerCase()) || r.description?.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat === 'all' || r.category === filterCat;
    const matchLang = filterLang === 'all' || r.language === filterLang || r.language === 'all' || !r.language;
    return matchSearch && matchCat && matchLang;
  });

  const categories = ['all', ...new Set(resources.map(r => r.category))];

  return (
    <div className="min-h-screen bg-background" data-testid="resources-page">
      <Navbar />
      <section className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-foreground mb-2" data-testid="resources-title">
              {l.title}
            </h1>
            <p className="text-muted-foreground text-base md:text-lg">{l.subtitle}</p>
          </motion.div>

          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder={l.search}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground"
                data-testid="resources-search" />
            </div>
            <select value={filterLang} onChange={e => setFilterLang(e.target.value)}
              className="px-3 py-2 rounded-xl bg-card border border-border text-sm text-foreground" data-testid="resources-lang-filter">
              <option value="all">{lang === 'fr' ? 'Toutes les langues' : 'All languages'}</option>
              <option value="fr">Français</option>
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="de">Deutsch</option>
              <option value="it">Italiano</option>
              <option value="pt">Português</option>
              <option value="nl">Nederlands</option>
              <option value="pl">Polski</option>
              <option value="tr">Türkçe</option>
              <option value="ru">Русский</option>
              <option value="ja">日本語</option>
              <option value="zh">中文</option>
              <option value="ar">العربية</option>
            </select>
          </div>
          <div className="flex gap-1.5 flex-wrap mb-6">
              {categories.map(cat => (
                <button key={cat} onClick={() => setFilterCat(cat)}
                  className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${filterCat === cat ? 'bg-blue-600 text-white' : 'bg-card border border-border text-muted-foreground hover:text-foreground'}`}
                  data-testid={`resources-filter-${cat}`}>
                  {cat === 'all' ? (lang === 'fr' ? 'Tout' : 'All') : (l.categories[cat] || cat)}
                </button>
              ))}
            </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-12" data-testid="resources-empty">{l.empty}</p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {filtered.map((r, i) => {
                const Icon = categoryIcons[r.category] || Tag;
                return (
                  <motion.div key={r.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                    className={`p-5 rounded-xl bg-card border ${r.pinned ? 'border-amber-500/30 bg-amber-500/5' : 'border-border hover:border-blue-500/30'} transition-colors group`} data-testid={`resource-${r.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4 text-blue-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-foreground font-medium text-sm mb-1 group-hover:text-blue-500 transition-colors">{r.title}</h3>
                        {r.description && <p className="text-muted-foreground text-xs line-clamp-2 mb-2">{r.description}</p>}
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{l.categories[r.category] || r.category}</span>
                          {r.url && (
                            <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:text-blue-400 flex items-center gap-1">
                              <ExternalLink className="w-3 h-3" /> Lien
                            </a>
                          )}
                        </div>
                        {r.content && (
                          <div className="mt-3 pt-3 border-t border-border">
                            <p className="text-muted-foreground text-xs whitespace-pre-line">{r.content}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
